import { type ClassValue, clsx } from 'clsx';

export function cn(...inputs: ClassValue[]) {
  return clsx(inputs);
}

export * from './attachmentProcessor';
export * from './imageSizing';
export * from './logger';

export function formatTime(date: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(date);
}

export function generateId(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }

  if (typeof globalThis.crypto?.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    globalThis.crypto.getRandomValues(bytes);
    return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  return `${Date.now().toString(36)}-fallback`;
}

/**
 * Escapes HTML characters to prevent XSS attacks
 * @param unsafe - The unsafe string that may contain HTML characters
 * @returns The escaped string safe for HTML rendering
 */
export function escapeHtml(unsafe: string): string {
  return unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

/**
 * Decodes HTML entities to their original characters
 * @param encoded - The string that may contain HTML entities
 * @returns The decoded string with HTML entities converted back to original characters
 */
export function decodeHtml(encoded: string): string {
  return encoded
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&#x27;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&rsquo;/g, "'")
    .replace(/&lsquo;/g, "'")
    .replace(/&rdquo;/g, '"')
    .replace(/&ldquo;/g, '"')
    .replace(/&nbsp;/g, " ")
    .replace(/&hellip;/g, "…")
    .replace(/&mdash;/g, "—")
    .replace(/&ndash;/g, "–")
    .replace(/&amp;/g, "&");
}

/**
 * Attempts to parse JSON and extract the text field
 * @param jsonString - The JSON string to parse
 * @returns The extracted text content or null if parsing fails
 */
function tryParseJsonText(jsonString: string): string | null {
  try {
    const parsed = JSON.parse(jsonString);
    if (parsed && typeof parsed === 'object' && 'text' in parsed) {
      return decodeHtml(parsed.text || "");
    }
  } catch {
    // JSON parsing failed
  }
  return null;
}

/**
 * Manually extracts text field value from malformed JSON
 * @param content - The content after "text":"
 * @returns The extracted and decoded text content
 */
function manuallyExtractTextContent(content: string): string {
  let result = '';
  let i = 0;
  
  while (i < content.length) {
    const char = content[i];
    
    if (char === '\\' && i + 1 < content.length) {
      const nextChar = content[i + 1];
      result += getEscapedCharacter(nextChar);
      i += 2;
    } else if (char === '"') {
      break; // End of string
    } else {
      result += char;
      i++;
    }
  }
  
  return decodeHtml(result);
}

/**
 * Converts escape sequences to actual characters
 * @param escapedChar - The character after the backslash
 * @returns The actual character representation
 */
function getEscapedCharacter(escapedChar: string): string {
  switch (escapedChar) {
    case 'n': return '\n';
    case 't': return '\t';
    case 'r': return '\r';
    case '"': return '"';
    case '\\': return '\\';
    default: return escapedChar;
  }
}

/**
 * Safely extracts message content from API responses that may contain malformed data
 * Handles cases where the text field contains raw JSON instead of message content
 * @param apiMessageText - The text field from the API message
 * @returns The actual message content, decoded from HTML entities
 */
export function extractMessageContent(apiMessageText: string | null | undefined): string {
  if (!apiMessageText) return "";
  
  try {
    const decodedText = decodeHtml(apiMessageText);
    const trimmedText = decodedText.trim();
    
    // Check if this looks like JSON containing a text field
    if (!trimmedText.startsWith('{') || !trimmedText.includes('"text"')) {
      return decodedText;
    }
    
    // Try standard JSON parsing first
    const jsonResult = tryParseJsonText(trimmedText);
    if (jsonResult !== null) {
      return jsonResult;
    }
    
    // Fallback to manual extraction for malformed JSON
    const textStart = trimmedText.indexOf('"text":"');
    if (textStart === -1) {
      return decodedText;
    }
    
    const contentStart = textStart + 8; // length of '"text":"'
    const content = trimmedText.substring(contentStart);
    return manuallyExtractTextContent(content);
    
  } catch (error) {
    console.error('Error processing API message text:', error);
    return decodeHtml(apiMessageText ?? "");
  }
}

export function simulateTypingDelay(): Promise<void> {
  // Simulate realistic typing delay between 1-3 seconds
  const minDelay = 1000;
  const maxDelay = 3000;
  const range = maxDelay - minDelay;
  const uint32MaxPlusOne = 0x100000000;

  let randomFraction = 0.5;
  if (typeof globalThis.crypto?.getRandomValues === 'function') {
    const values = new Uint32Array(1);
    globalThis.crypto.getRandomValues(values);
    randomFraction = values[0] / uint32MaxPlusOne;
  }

  const delay = minDelay + Math.floor(randomFraction * range);
  return new Promise(resolve => setTimeout(resolve, delay));
}

/**
 * Converts a base64 image string to a Blob object
 * @param base64String - The base64 encoded image string (with or without data URI prefix)
 * @param mimeType - Optional MIME type (will be extracted from data URI if present)
 * @returns Promise<Blob> - The resulting blob
 */
export function base64ToBlob(base64String: string, mimeType?: string): Promise<Blob> {
  return new Promise((resolve, reject) => {
    try {
      let actualBase64: string;
      let actualMimeType: string;

      // Check if it's a data URI (data:image/png;base64,...)
      if (base64String.startsWith('data:')) {
        const [header, data] = base64String.split(',');
        actualBase64 = data;
        
        // Extract MIME type from header
        const mimeMatch = /data:([^;]+)/.exec(header);
        actualMimeType = mimeType || (mimeMatch ? mimeMatch[1] : 'image/png');
      } else {
        actualBase64 = base64String;
        actualMimeType = mimeType || 'image/png';
      }

      // Convert base64 to binary
      const byteCharacters = atob(actualBase64);
      const byteNumbers = new Array(byteCharacters.length);
      
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.codePointAt(i) ?? 0;
      }
      
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: actualMimeType });
      
      resolve(blob);
    } catch (error) {
      reject(new Error(`Failed to convert base64 to blob: ${error}`));
    }
  });
}

/**
 * Converts a Blob back to a base64 data URI string
 * @param blob - The blob to convert
 * @returns Promise<string> - The resulting base64 data URI
 */
export function blobToBase64DataUri(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to read blob as data URI'));
      }
    };
    reader.onerror = () => reject(new Error('Error reading blob'));
    reader.readAsDataURL(blob);
  });
}

/**
 * Converts an array of base64 image strings to an array of blobs
 * @param base64Images - Array of base64 image strings
 * @returns Promise<Blob[]> - Array of resulting blobs
 */
export async function convertBase64ImagesToBlobs(base64Images: string[]): Promise<Blob[]> {
  const conversionPromises = base64Images.map(base64String => base64ToBlob(base64String));
  return Promise.all(conversionPromises);
}

/**
 * Creates an object URL from a base64 image string for displaying in img elements
 * @param base64String - The base64 encoded image string
 * @param mimeType - Optional MIME type
 * @returns Promise<string> - The object URL
 */
export async function createImageObjectUrl(base64String: string, mimeType?: string): Promise<string> {
  const blob = await base64ToBlob(base64String, mimeType);
  return URL.createObjectURL(blob);
}

/**
 * Utility function to validate if a string is a valid base64 image
 * @param base64String - The string to validate
 * @returns boolean - Whether the string is a valid base64 image
 */
export function isValidBase64Image(base64String: string): boolean {
  try {
    // Check if it's a data URI
    if (base64String.startsWith('data:image/')) {
      const [, data] = base64String.split(',');
      if (!data) return false;
      
      // Try to decode the base64 part
      atob(data);
      return true;
    }
    
    // Try to decode as raw base64
    atob(base64String);
    return true;
  } catch {
    return false;
  }
}
